import { getBackendUrl } from "@Utils";
import { useEffect, useState, lazy, Suspense, createElement } from "react";
import { Route, useLocation, useNavigate, Routes, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import css from "./App.module.css";
import { getCheckIfAuthenticated, getUserDetails } from "./actions/userDetails";
import LicenceWarning from "./components/LicenceWarning/LicenceWarning";
import { getUserPermissions } from "./actions/userPermissions";
import { fetchLayout } from "./actions/layout";
import MinimizeFloatter from "@Components/MinimizeFloatter";
import { getPortalData } from "@Actions/portalSetting";
import * as Sentry from "@sentry/react";
import OldBrowserWarning from "@Components/OldBrowserWarning";
import Notification from "./components/Notification";
import MobileDeviceRestrict from "@Components/MobileDeviceRestrict";
import { getCurrProjectData } from "./actions/breadcrumbsdata";
import axiosWrapper from "@Services/axiosWrapper";
import { getPortalObject, setHelpSectionState } from "./actions/portalDashboard.js";
import StickySidebar from "./components/StickySidebar";
import { setShowStickySidebar } from "./actions/stickySidebar.js";
import ErrorBoundary from "./ErrorBoundary";
import routes from "./routes";
import { getPortalCustomModules } from "@Actions/portalSetting.helper";
import { setPreferredLanguage } from "./reducers/preferredLanguage";
import _ from "lodash";
import SplitLayout from "@CubeComponents/html/SplitLayout/SplitLayout";
import HelpSection from "./HelpSection";
import { setRenderHeader } from "./reducers/portalSetting";
import { addRedirectParam } from "@Helpers/urlHelpers";
import { setSigninData } from "./reducers/signinData";
import Text from "@CubeComponents/html/Text/Text";

const FeedbackToastComponent = lazy(() => import("./cubeComponent/html/feedbackToast"));
const FileSync = lazy(() => import("./components/FilesSync"));
const PortalIllustration = lazy(() => import("./components/PortalIllustration"));
const Header = lazy(() => import("@Components/Header"));
const FileUpload = lazy(() => import("./components/FileManager/FileUpload/FileUpload"));

function Router() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    licenceStatus,
    userDetails,
    // fileSyncState,
    showStickySidebar,
    fileUploadState,
    helpSectionState,
    renderHeader,
    portalData,
    checkIfAuthenticated,
    signinData,
  } = useSelector((state: any) => state);

  const [isPortalExpired, setIsPortalExpired] = useState(false);
  const status = localStorage.getItem("isLogedIn");
  const location: any = useLocation();
  const navigate = useNavigate();
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [showLogoutError, setShowLogoutError] = useState("");
  const [showOldBrowserWarning, setShowOldBrowserWarning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [splitterSize, setSplitterSize] = useState<any>([100, 0]);
  const detectMobileDevice = () => {
    if (
      !["techm-prod", "techm-preprod", "techm-dev"].includes(process.env.REACT_APP_ENV as string) &&
      !globalThis.location.host.includes("dpmsportal")
    ) {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
        !globalThis.location.href.includes("mobileExplorer")
      ) {
        setIsMobileDevice(true);
      } else {
        setIsMobileDevice(false);
      }
    }
  };

  useEffect(() => {
    const errorMessage = searchParams.get("errorMessage");
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("errorMessage");

    if (errorMessage) {
      setSearchParams(newSearchParams);
      setShowLogoutError(decodeURIComponent(errorMessage));
    }
  }, [searchParams.get("errorMessage")]);

  useEffect(() => {
    const projectId = searchParams.get("projectId");
    if (projectId) {
      dispatch(getCurrProjectData(projectId));
    }
  }, [searchParams.get("projectId")]);

  useEffect(() => {
    validateReturnRouter();
  }, [portalData, location.pathname, userDetails.id]);

  const validateReturnRouter = async () => {
    if (userDetails.id) {
      if (
        portalData.isExpired &&
        !location.pathname.includes("signupflow") &&
        location.pathname != "/newInvitation" &&
        userDetails.portal?.id
      ) {
        setIsPortalExpired(true);
      } else if (!userDetails.portal?.id && !["/", "/signupflow", "/newInvitation"].includes(location.pathname)) {
        const signinData = await axiosWrapper.post(`${getBackendUrl()}/accounts/getSigninData`, {});
        if (globalThis.location.host != signinData.data.frontendUrl) {
          const newUrl: any = addRedirectParam({
            targetPath: new URL("/", globalThis.location.origin),
            redirectTo: location,
          });
          navigate(newUrl);
        }
      } else {
        setIsPortalExpired(false);
      }
    } else {
      setIsPortalExpired(false);
    }
  };

  useEffect(() => {
    if (location.pathname.includes("null") || location.search.includes("null")) {
      globalThis.location.href = "/";
    }
  }, [location]);

  const getSigninData = async () => {
    const signinData = await axiosWrapper.post(`${getBackendUrl()}/accounts/getSigninData`, {});
    dispatch(setSigninData({ data: signinData.data }));
  };

  useEffect(() => {
    dispatch(getCheckIfAuthenticated());
    getSigninData();
  }, []);

  useEffect(() => {
    if (userDetails.id && portalData._id && userDetails.portal?.id) {
      dispatch(getCheckIfAuthenticated());
    }
  }, [userDetails.id, portalData._id]);

  useEffect(() => {
    if (checkIfAuthenticated !== null) {
      if (_.get(checkIfAuthenticated, "data.status")) {
        dispatch(
          getUserPermissions({
            resourceType: ["RESOURCE_TYPE_PORTAL"],
          }),
        );
      } else {
        setIsPortalExpired(false);
      }
    }
  }, [_.get(checkIfAuthenticated, "data.status")]);

  useEffect(() => {
    if (_.get(checkIfAuthenticated, "data.status") && (!userDetails.id || !userDetails.portal?.id)) {
      dispatch(getUserDetails());
    }
  }, [_.get(checkIfAuthenticated, "data.status"), userDetails.id, _.get(userDetails, "portal.id")]);

  useEffect(() => {
    if (userDetails.id && portalData._id && userDetails.portal?.id) {
      dispatch(getPortalObject());
      dispatch(getPortalCustomModules());
    }
  }, [userDetails.id, portalData._id]);

  useEffect(() => {
    if (process.env.REACT_APP_SENTRY_DSN && userDetails.id) {
      Sentry.setUser({ email: userDetails.email, id: userDetails.id });
    }
  }, [userDetails]);

  const checkBrowserCompatibility = () => {
    if (typeof Array.prototype.at != "function" || typeof Array.prototype.toSorted != "function") {
      setShowOldBrowserWarning(true);
    }
  };
  useEffect(() => {
    if (
      _.get(checkIfAuthenticated, "data.status") &&
      globalThis.location.host == _.get(signinData, "data.frontendUrl") &&
      !["/signupflow", "/reset-password", "/"].includes(location.pathname)
    ) {
      const newUrl = addRedirectParam({
        targetPath: new URL("/", globalThis.location.origin),
        redirectTo: location,
      });
      navigate(newUrl);
    }
  }, [_.get(checkIfAuthenticated, "data.status"), signinData, location.pathname]);

  const updateLanguage = async () => {
    let response;
    try {
      response = await axiosWrapper.post(`${getBackendUrl()}/accounts/get`, {
        projection: ["preferredLanguage"],
      });
    } catch (e: any) {
      if (_.get(e, "response.data.errorCode") !== 7018) {
        Promise.reject("User is not assigned a language in this portal");
      }
    } finally {
      const newLanguage = _.get(response, "data.data.preferredLanguage") ?? "en";
      dispatch(setPreferredLanguage(newLanguage));
      if (["ar", "he", "fa", "ur"].includes(newLanguage)) {
        document.body.setAttribute("dir", "rtl");
      }
    }
  };
  useEffect(() => {
    if (_.get(checkIfAuthenticated, "data.status")) {
      updateLanguage();
    } else if (checkIfAuthenticated !== null && !_.get(checkIfAuthenticated, "data.status")) {
      dispatch(setPreferredLanguage("en"));
    }
  }, [_.get(checkIfAuthenticated, "data.status")]);

  useEffect(() => {
    checkBrowserCompatibility();
  }, []);

  useEffect(() => {
    detectMobileDevice();
  }, [location]);

  useEffect(() => {
    if (location.pathname != "/") {
      setShowLogoutError("");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!location.pathname.includes("googleSigninButton")) {
      dispatch(fetchLayout());
      dispatch(getPortalData());
    }
  }, []);

  const matchRoute = (pathname: any, path: any) => {
    const regex = new RegExp("^/?" + path.replaceAll(/:[^\s/]+/g, String.raw`([\w-]+)`) + "$");
    return regex.test(pathname);
  };

  useEffect(() => {
    const currentRoute = routes.find((route) => route.path.some((path) => matchRoute(location.pathname, path)));
    if (currentRoute?.showStickySidebar === false) {
      dispatch(setShowStickySidebar(false));
    }
    if (currentRoute?.showHeader === false) {
      dispatch(setRenderHeader(false));
    } else {
      dispatch(setRenderHeader(true));
    }
    if (location.pathname === "/" && status === "true") {
      return;
    }
    if (
      location.pathname !== "/" &&
      status !== "true" &&
      !location.pathname.includes("thank-you") &&
      !location.pathname.includes("googleSigninButton") &&
      !location.pathname.includes("googleLoginCallback") &&
      !location.pathname.includes("resetPassword") &&
      !location.pathname.includes("95789") &&
      !location.pathname.includes("signup") &&
      !location.pathname.includes("invitation") &&
      !location.pathname.includes("pdfExport") &&
      !location.pathname.includes("newSignIn") &&
      !location.pathname.includes("reset-password") &&
      !location.pathname.includes("newInvitation")
    ) {
      navigate(
        `/?redirectUrl=${encodeURIComponent(globalThis.location.pathname + globalThis.location.search)}${globalThis.location.hash}`,
      );
    }
  }, [location.pathname]);

  useEffect(() => {
    if (helpSectionState) {
      setSplitterSize([70, 30]);
    } else {
      setSplitterSize([100, 0]);
    }
  }, [helpSectionState]);

  const handleHelpSectionClick = () => {
    dispatch(setHelpSectionState(!helpSectionState));
  };

  const getContent = () => {
    return (
      <SplitLayout
        initialSizes={splitterSize}
        gutterWidth={"2px"}
        minSizes={[10, 20]}
        style={{ display: "flex", height: "100%" }}
        showProperties={true}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => setIsDragging(false)}
      >
        {[
          <div key={"mainContent"} className="flex flex-col overflow-auto h-full w-full">
            {renderHeader && (
              <Suspense fallback={<></>}>
                <Header />
              </Suspense>
            )}
            <div className="flex overflow-auto h-full w-full">
              {showStickySidebar && <StickySidebar userData={undefined} />}
              <div className="w-full h-full overflow-auto">
                <ErrorBoundary location={location}>
                  <Routes>
                    {routes.flatMap(({ path, element }: any) =>
                      path.map((singlePath: any) => (
                        <Route
                          key={singlePath}
                          path={singlePath}
                          element={<Suspense fallback={<></>}>{createElement(element, singlePath.props)}</Suspense>}
                        />
                      )),
                    )}
                  </Routes>
                </ErrorBoundary>
              </div>
            </div>
          </div>,
          helpSectionState && <HelpSection isDragging={isDragging} handleHelpSectionClick={handleHelpSectionClick} />,
        ].filter(Boolean)}
      </SplitLayout>
    );
  };
  return (
    <>
      {showLogoutError && (
        <div className="flex justify-around text-white p-1 bg-system_colour_3">
          <Text>{showLogoutError}</Text>
        </div>
      )}
      <Suspense fallback={<></>}>
        <Suspense fallback={<></>}>
          <FeedbackToastComponent />
        </Suspense>
      </Suspense>

      {showOldBrowserWarning && (
        <Suspense fallback={<></>}>
          <OldBrowserWarning />
        </Suspense>
      )}

      {isMobileDevice && (
        <Suspense fallback={<></>}>
          <MobileDeviceRestrict />
        </Suspense>
      )}

      <Suspense fallback={<></>}>
        {userDetails.id && portalData._id && (
          <Suspense fallback={<></>}>
            <FileSync />
          </Suspense>
        )}
      </Suspense>
      <div className={`${!location.pathname.includes("pdfExport") && css.controlSection}`}>
        {!licenceStatus && <LicenceWarning />}
        <MinimizeFloatter />
        {userDetails.id && portalData._id && <Notification />}
        {(fileUploadState == "MAXIMISED" || fileUploadState == "MINIMISED") && (
          <Suspense fallback={<></>}>
            <FileUpload />
          </Suspense>
        )}
        {!isPortalExpired && getContent()}
        {isPortalExpired && (
          <Suspense fallback={<></>}>
            <Suspense fallback={<></>}>
              <PortalIllustration />
            </Suspense>
          </Suspense>
        )}
      </div>
    </>
  );
}

export default Router;
